import { z } from "zod";

/**
 * Phone alerts the team app can receive. The id is stored in
 * team_alert_prefs.alert_type, so never rename one; add a new id instead.
 */
export const TEAM_ALERT_TYPES = [
  "new_booking",
  "pending_reminder",
  "guest_cancelled",
  "guest_changed",
  "new_review",
  "creator_application",
  "ambassador_application",
  "guest_running_late",
  "starting_soon_pending",
  "stay_failed",
  "partner_lead",
  "ambassador_payout_due",
  "milestone",
  "invoice_overdue",
  "expenses_over_budget",
  "search_miss",
  "ads_no_bookings",
  "new_signup",
] as const;
export type TeamAlertType = (typeof TEAM_ALERT_TYPES)[number];

/** English only: the team app is an internal tool (owner, 2026-10-22). */
export const TEAM_ALERT_LABELS: Record<TeamAlertType, string> = {
  new_booking: "New booking",
  pending_reminder: "Not confirmed in 30 min",
  guest_cancelled: "Guest cancelled",
  guest_changed: "Guest changed a booking",
  new_review: "New review",
  creator_application: "Creator application",
  ambassador_application: "Ambassador application",
  guest_running_late: "Guest running late",
  starting_soon_pending: "Starts within 24h, still pending",
  stay_failed: "Stay booking failed",
  partner_lead: "Partner lead",
  ambassador_payout_due: "Ambassador payouts due",
  milestone: "Milestones",
  invoice_overdue: "Invoice overdue",
  expenses_over_budget: "Expenses over budget",
  search_miss: "Searches with no results",
  ads_no_bookings: "Ads spending, no bookings",
  new_signup: "New account",
};

export type TeamAlertPrefs = Record<TeamAlertType, boolean>;

/** No row in team_alert_prefs means the alert is on. */
export function defaultTeamAlertPrefs(): TeamAlertPrefs {
  return Object.fromEntries(TEAM_ALERT_TYPES.map((t) => [t, true])) as TeamAlertPrefs;
}

const count = z.number().int().min(0).max(500);

/**
 * The team app's "edit booking": date, time and head counts. The server
 * prices it; the client never sends a total. `dryRun` returns the new total
 * without saving, so the screen can show old vs new before the save.
 */
export const reservationChangeSchema = z
  .object({
    reservationDate: z.iso.date().optional(),
    reservationTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .optional(),
    adults: count.optional(),
    children: count.optional(),
    infants: count.optional(),
    hours: count.optional(),
    nights: count.optional(),
    groups: count.optional(),
    dryRun: z.boolean().optional(),
  })
  .refine(
    (v) => Object.entries(v).some(([k, val]) => k !== "dryRun" && val !== undefined),
    { message: "nothing to change" },
  );
export type ReservationChangeInput = z.infer<typeof reservationChangeSchema>;

export interface ReservationChangeQuote {
  previousPrice: number;
  price: number;
  previousCommission: number;
  commission: number;
}
